import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/client";
import Ornament from "./components/Ornament";
import Icon from "./components/Icon";
import "./Homepage.css";

const experienceOptions = [
    "Fresher (less than 1 year)",
    ...Array.from({ length: 30 }, (_, i) => `${i + 1} year${i === 0 ? '' : 's'}`)
];

const AUDIENCES = [
    { icon: 'users',    title: 'Individuals & Families',     blurb: 'Scholars, students, priests, professionals, householders.' },
    { icon: 'flame',    title: 'Veda Schools & Pāṭhaśālās',  blurb: 'Traditional adhyayana — Saṁhitā to Ghana and beyond.' },
    { icon: 'shield',   title: 'Temples & Maṭhas',           blurb: 'Ritual, seva, archakas, ṛtviks, administrative roles.' },
    { icon: 'sparkle',  title: 'Universities & Academia',    blurb: 'Sanskrit, Indology, Āyurveda, Philosophy departments.' },
    { icon: 'briefcase',title: 'Dharmik Businesses',         blurb: 'Publishers, heritage services, wellness, artisan enterprises.' },
    { icon: 'spark',    title: 'Creators & Media',           blurb: 'Writers, podcasters, translators, documentary teams.' },
];

const Homepage = () => {
    const [skills, setSkills] = useState("");
    const [experience, setExperience] = useState("");
    const [location, setLocation] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [recentJobs, setRecentJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRecentJobs = async () => {
            try {
                const data = await api.get("/api/job/basicinfo");
                const sortedJobs = (data || [])
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .slice(0, 6);
                setRecentJobs(sortedJobs);
            } catch (error) {
                console.error("Error fetching recent jobs:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRecentJobs();
    }, []);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (skills) params.append("skills", skills);
        if (experience) params.append("experience", experience);
        if (location) params.append("location", location);
        navigate(`/job-listings?${params.toString()}`);
    };

    const handleViewDetails = (jobId) => {
        const token = localStorage.getItem("token");
        if (!token) navigate("/signin");
        else navigate(`/job-details/${jobId}`);
    };

    return (
        <div className="samudyoga-home">

            <section className="home-hero sw-aurora">
                <div className="home-hero-inner">
                    <img
                        src="/samudyoga-logo.png"
                        alt="Samudyoga · Job Portal under Swadharma Project · A Dharma Poshanam initiative"
                        className="home-hero-logo"
                    />
                    <Ornament variant="rule" tone="gold" size={160} className="home-hero-rule" />
                    <span className="home-eyebrow">Project Samudyoga · सम्+उद्योग</span>
                    <h1 className="home-title">
                        Opportunities for Dharmik talent,<br />
                        services &amp; institutions <em>worldwide</em>
                    </h1>
                    <p className="home-subtitle">
                        Connecting scholars, priests, creators, businesses and institutions —
                        one network, anchored in tradition. An independent, non-sectarian
                        initiative under <strong>Swadharma PRO</strong>.
                    </p>

                    <form
                        className="home-search"
                        onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
                        role="search"
                        aria-label="Search opportunities"
                    >
                        <div className="home-search-field">
                            <label htmlFor="search-skills">Skills / role</label>
                            <div className="home-search-input">
                                <Icon name="search" size={18} stroke="var(--sw-muted)" />
                                <input
                                    id="search-skills"
                                    type="text"
                                    placeholder="Sanskrit, Āyurveda, Software…"
                                    value={skills}
                                    onChange={(e) => setSkills(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="home-search-field">
                            <label htmlFor="search-exp">Experience</label>
                            <div
                                className="home-dd"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter') setIsDropdownOpen(!isDropdownOpen); }}
                            >
                                <input
                                    id="search-exp"
                                    type="text"
                                    placeholder="Select"
                                    value={experience}
                                    readOnly
                                />
                                <span className="home-dd-caret" aria-hidden="true">▾</span>
                                {isDropdownOpen && (
                                    <div className="home-dd-menu" role="listbox">
                                        {experienceOptions.map((option) => (
                                            <div
                                                key={option}
                                                className="home-dd-item"
                                                role="option"
                                                aria-selected={experience === option}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExperience(option);
                                                    setIsDropdownOpen(false);
                                                }}
                                            >
                                                {option}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="home-search-field">
                            <label htmlFor="search-loc">Location</label>
                            <div className="home-search-input">
                                <Icon name="location" size={18} stroke="var(--sw-muted)" />
                                <input
                                    id="search-loc"
                                    type="text"
                                    placeholder="City or country"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                />
                            </div>
                        </div>
                        <button type="submit" className="sw-btn sw-btn-saffron sw-btn-lg home-search-btn">
                            <Icon name="search" size={18} />
                            <span>Search</span>
                        </button>
                    </form>
                </div>

                <div className="home-stats" aria-label="Network statistics">
                    {[
                        { v: '10+',    l: 'Partner Institutions' },
                        { v: '1,200+', l: 'Verified Profiles' },
                        { v: '200+',   l: 'Active Opportunities' },
                        { v: '28',     l: 'Countries' },
                    ].map((s) => (
                        <div key={s.l} className="home-stat">
                            <div className="home-stat-value">{s.v}</div>
                            <div className="home-stat-label">{s.l}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="home-section">
                <div className="sw-section-mark">Recent Opportunities</div>
                <div className="home-section-header">
                    <h2>Latest postings from partner institutions</h2>
                    <button
                        className="sw-btn sw-btn-ghost sw-btn-sm"
                        onClick={() => navigate('/job-listings')}
                    >
                        <span>View all</span>
                        <Icon name="arrow" size={16} />
                    </button>
                </div>

                {isLoading ? (
                    <div className="home-loading">
                        <Ornament variant="lotus" tone="gold" size={48} />
                        <p>Loading opportunities…</p>
                    </div>
                ) : recentJobs.length === 0 ? (
                    <div className="home-empty">
                        <Ornament variant="flourish" tone="gold" size={56} />
                        <h3>No opportunities yet</h3>
                        <p>Check back soon — partner institutions are joining the network.</p>
                        <button className="sw-btn sw-btn-ghost sw-btn-sm" onClick={() => navigate('/signup')}>
                            Register as a partner
                        </button>
                    </div>
                ) : (
                    <div className="home-jobs-grid">
                        {recentJobs.map((job) => (
                            <article key={job._id} className="home-job-card">
                                <div className="home-job-head">
                                    <h3>{job.title || 'Untitled role'}</h3>
                                    <span className="sw-tag sw-tag-saffron">New</span>
                                </div>
                                <div className="home-job-meta">
                                    <div className="home-job-meta-row">
                                        <Icon name="location" size={16} stroke="var(--sw-muted)" />
                                        <span>{job.location || 'Not specified'}</span>
                                    </div>
                                    <div className="home-job-meta-row">
                                        <Icon name="rupee" size={16} stroke="var(--sw-muted)" />
                                        <span>{job.salary || 'Not disclosed'}</span>
                                    </div>
                                    <div className="home-job-meta-row sw-text-muted home-job-posted">
                                        <Icon name="calendar" size={14} stroke="var(--sw-muted)" />
                                        <span>Posted {new Date(job.createdAt).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <button
                                    className="sw-btn sw-btn-navy sw-btn-sm home-job-cta"
                                    onClick={() => handleViewDetails(job._id)}
                                >
                                    <span>View &amp; apply</span>
                                    <Icon name="arrow" size={14} />
                                </button>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <section className="home-audience-section">
                <div className="home-audience">
                    <div className="sw-section-mark">Who Samudyoga is for</div>
                    <h2>A network for every Dharmik vocation</h2>
                    <p className="home-audience-lead">
                        Samudyoga gathers traditional, academic, professional, and creative
                        Dharmik work into one place — preserving distinctions of tradition
                        while bridging across geography.
                    </p>

                    <div className="home-audience-grid">
                        {AUDIENCES.map((a) => (
                            <div key={a.title} className="home-audience-card">
                                <div className="home-audience-icon">
                                    <Icon name={a.icon} size={22} stroke="var(--sw-saffron-dark)" />
                                </div>
                                <strong>{a.title}</strong>
                                <p>{a.blurb}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Homepage;
